import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function CreateActivity() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [f, setF] = useState({
    title: "",
    category: "social",
    is_online: false,
    description: "",
    location: "",
    city: "",
    country: "",
    start_at: "",
    capacity: 4,
    price: 0,
    age_min: "",
    age_max: "",
    gender_pref: "",
    energy_pref: "",
    cover_image: "",
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t("profile.fileTooLarge", { max: "10MB" }));
      return;
    }
    
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/media", fd, {
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        }
      });
      setF((p) => ({ ...p, cover_image: data.url }));
      toast.success("Cover image uploaded!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed, please try again");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const COUNTRY_PRESETS = [
    "Germany","United States","United Kingdom","France","Spain","Italy",
    "Netherlands","Sweden","Portugal","Belgium","Switzerland","Austria",
    "Denmark","Norway","Finland","Poland","Ireland","Canada","Australia",
    "Brazil","Mexico","Argentina","India","Japan","South Korea","Singapore",
    "United Arab Emirates","Saudi Arabia","Turkey","Egypt","Morocco",
    "South Africa","Nigeria","Kenya",
  ];

  const CATEGORIES = [
    { id: "sports", label: t("createActivity.catSports"), color: "#E2F3E4" },
    { id: "social", label: t("createActivity.catSocial"), color: "#FFE4E1" },
    { id: "educational", label: t("createActivity.catStudy"), color: "#EBE4FF" },
    { id: "entertainment", label: t("createActivity.catFun"), color: "#FFF0D4" },
  ];

  const upd = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setF((p) => ({ ...p, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.is_online) {
      if (!f.country.trim()) { toast.error(t("createActivity.countryRequired")); return; }
      if (!f.city.trim()) { toast.error(t("createActivity.cityRequired")); return; }
    }
    setBusy(true);
    try {
      const payload = {
        ...f,
        city: f.is_online ? null : f.city.trim(),
        country: f.is_online ? null : f.country.trim(),
        capacity: Number(f.capacity),
        price: Number(f.price) || 0,
        age_min: f.age_min ? Number(f.age_min) : null,
        age_max: f.age_max ? Number(f.age_max) : null,
        start_at: f.start_at ? new Date(f.start_at).toISOString() : null,
        gender_pref: f.gender_pref || null,
        energy_pref: f.energy_pref || null,
      };
      const { data } = await api.post("/activities", payload);
      toast.success(t("createActivity.postedToast"));
      nav(`/activity/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "activity_limit_reached") {
        toast.error(t("createActivity.limitReached", { limit: detail.limit ?? 3 }));
        setTimeout(() => nav("/mine"), 1500);
      } else {
        toast.error(fmtError(detail) || e.message);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      <button onClick={() => nav(-1)} className="btn-nb mb-5" data-testid="back-btn"><ArrowLeft className="w-4 h-4" strokeWidth={3}/> {t("common.back")}</button>
      <h1 className="font-display font-black text-4xl sm:text-5xl mb-1">{t("createActivity.newActivity")} <span className="text-[#FF6B6B]">{t("createActivity.activityWord")}</span></h1>
      <p className="text-[#4A4A4A] mb-6">{t("createActivity.subtitle")}</p>

      <form onSubmit={submit} className="card-nb p-6 bg-white space-y-4" data-testid="create-form">
        <div>
          <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.titleField")}</label>
          <input className="input-nb" required maxLength={80} value={f.title} onChange={upd("title")} placeholder={t("createActivity.titlePh")} data-testid="create-title"/>
        </div>

        <div>
          <label className="block text-xs font-display font-bold uppercase tracking-widest mb-2">{t("createActivity.category")}</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {CATEGORIES.map((c) => (
              <button key={c.id} type="button" onClick={() => setF((p) => ({ ...p, category: c.id }))} data-testid={`create-cat-${c.id}`}
                className={`px-3 py-2 rounded-xl border-2 border-[#121212] font-display font-bold text-sm ${f.category === c.id ? "shadow-nb-sm -translate-x-[1px] -translate-y-[1px]" : ""}`}
                style={{ background: c.color }}>{c.label}</button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="create-online-toggle">
            <input type="checkbox" checked={f.is_online} onChange={upd("is_online")} className="w-5 h-5 accent-[#FF6B6B]"/>
            <span className="font-bold">{t("createActivity.online")}</span>
          </label>
        </div>

        <div>
          <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.description")}</label>
          <textarea className="input-nb min-h-[100px]" maxLength={600} value={f.description} onChange={upd("description")} placeholder={t("createActivity.descPh")} data-testid="create-desc"/>
        </div>

        <div>
          <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">Cover Image</label>
          {f.cover_image ? (
            <div className="relative rounded-2xl overflow-hidden border-2 border-[#121212] shadow-nb-sm h-48">
              <img src={f.cover_image} alt="Cover Preview" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => setF((p) => ({ ...p, cover_image: "" }))}
                className="absolute top-2 right-2 w-8 h-8 bg-white border-2 border-[#121212] rounded-full flex items-center justify-center hover:bg-[#FFE6E0] transition-colors shadow-nb-sm"
              >
                <X className="w-4 h-4 text-[#121212]" strokeWidth={3} />
              </button>
            </div>
          ) : (
            <>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id="activity-cover-upload"
                onChange={handleImageUpload}
                disabled={uploading}
              />
              <button
                type="button"
                disabled={uploading}
                onClick={() => document.getElementById("activity-cover-upload").click()}
                className="w-full h-32 border-2 border-dashed border-[#121212]/20 rounded-2xl flex flex-col items-center justify-center bg-[#FAF8F2] hover:bg-[#F4F1E8] transition-colors cursor-pointer"
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin text-[#FF6B6B]" />
                    <span className="text-sm font-bold text-[#4A4A4A]">Uploading {progress}%...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-6 h-6 text-[#4A4A4A]" strokeWidth={2.5} />
                    <span className="text-sm font-bold text-[#121212]">Upload a Cover Image</span>
                    <span className="text-xs text-[#7C828D]">Supported formats: JPG, PNG (Max 10MB)</span>
                  </div>
                )}
              </button>
            </>
          )}
        </div>

        {!f.is_online && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.country")} *</label>
                <input
                  list="country-presets"
                  className="input-nb"
                  required
                  value={f.country}
                  onChange={upd("country")}
                  placeholder={t("createActivity.countryPh")}
                  data-testid="create-country"
                />
                <datalist id="country-presets">
                  {COUNTRY_PRESETS.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.city")} *</label>
                <input
                  className="input-nb"
                  required
                  value={f.city}
                  onChange={upd("city")}
                  placeholder={t("createActivity.cityPh")}
                  data-testid="create-city"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.venueOptional")}</label>
              <input className="input-nb" value={f.location} onChange={upd("location")} placeholder={t("createActivity.locationPh")} data-testid="create-location"/>
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.whenOptional")}</label>
            <input type="datetime-local" className="input-nb" value={f.start_at} onChange={upd("start_at")} data-testid="create-start"/>
          </div>
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.capacity")}</label>
            <input type="number" min={1} max={200} className="input-nb" required value={f.capacity} onChange={upd("capacity")} data-testid="create-capacity"/>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.price")}</label>
            <input type="number" min={0} step="1" className="input-nb" value={f.price} onChange={upd("price")} data-testid="create-price"/>
          </div>
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.ageMin")}</label>
            <input type="number" className="input-nb" value={f.age_min} onChange={upd("age_min")} data-testid="create-age-min"/>
          </div>
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.ageMax")}</label>
            <input type="number" className="input-nb" value={f.age_max} onChange={upd("age_max")} data-testid="create-age-max"/>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.genderPref")}</label>
            <select className="input-nb" value={f.gender_pref} onChange={upd("gender_pref")} data-testid="create-gender-pref">
              <option value="">{t("createActivity.any")}</option>
              <option value="female">{t("createActivity.female")}</option>
              <option value="male">{t("createActivity.male")}</option>
              <option value="other">{t("createActivity.other")}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("createActivity.energyPref")}</label>
            <select className="input-nb" value={f.energy_pref} onChange={upd("energy_pref")} data-testid="create-energy-pref">
              <option value="">{t("createActivity.any")}</option>
              <option value="energetic">{t("profile.energetic")}</option>
              <option value="calm">{t("profile.calm")}</option>
              <option value="balanced">{t("profile.balanced")}</option>
            </select>
          </div>
        </div>

        <button type="submit" disabled={busy} className="btn-nb btn-nb-primary w-full text-base" data-testid="create-submit">
          {busy ? t("createActivity.posting") : t("createActivity.post")}
        </button>
      </form>
    </div>
  );
}
